/**
 * The optional per-template LinkedIn issuing organisation — the browser's twin.
 *
 * The reasoning is written out in full in `backend/src/domain/linkedin/issuing-organization.ts`:
 * what this value changes (the «Veren kuruluş» field of the LinkedIn profile form) and what it
 * does not (the issuer of record, the verification page's «Veren kurum» row, the document's
 * drawing, the e-mail sender, the account a post is published from); why the name, the Page and
 * the verified numeric id are ONE identity that is taken whole or not at all; and why no id is
 * ever derived from a name or a slug. Read it there.
 *
 * Two modules because they live in two packages that do not import each other, kept honest by one
 * shared file of cases: `tests/fixtures/linkedin-issuing-organization-vectors.json`, which BOTH
 * suites assert against. Change one implementation without the other and that fixture fails.
 */

import {
  type AddToProfileLink,
  addToProfileLinkFor,
  type CredentialForLinkedIn,
} from './linkedin-add-to-profile';
import { verifiedOrganizationIdForPage } from './linkedin-organization';

/** The same shape the backend's `FieldError` has, restated so this module imports nothing. */
export interface IssuingOrganizationFieldError {
  key: string;
  code: string;
  message: string;
}

/** The key the override lives under, inside a template's namespaced `layout.v07` block. */
export const LINKEDIN_ISSUER_KEY = 'linkedInIssuer';

/** The same length the issuer profile's own names are held to. */
const MAX_NAME = 512;

export interface LinkedInIssuingOrganization {
  /** What LinkedIn's «Veren kuruluş» field should say. Never empty when the override exists. */
  organizationName: string;
  /** The organisation's own LinkedIn company Page, normalised, or null when none was given. */
  pageUrl: string | null;
}

export type IssuingOrganizationSource = 'template' | 'issuer' | 'unreadable';

export interface ResolvedIssuingOrganization {
  /**
   * Which identity answered — never a mixture of two, and `unreadable` when neither may.
   *
   * `unreadable` is not an identity: its name is empty and its id is null on purpose, so a caller
   * that forgets to check cannot put anything in front of a member. It means the record's stored
   * organisation is broken and the LinkedIn profile-add has to be withheld.
   */
  source: IssuingOrganizationSource;
  organizationName: string;
  pageUrl: string | null;
  /** The verified numeric id of THAT identity's own Page, or null. */
  organizationId: string | null;
}

export type IssuingOrganizationValidation =
  | { ok: true; value: LinkedInIssuingOrganization | null }
  | { ok: false; errors: IssuingOrganizationFieldError[] };

const NAME_KEY = `${LINKEDIN_ISSUER_KEY}.organizationName`;
const PAGE_KEY = `${LINKEDIN_ISSUER_KEY}.pageUrl`;

/**
 * One of the two boxes, read from free-form JSON.
 *
 * «Absent» and «wrong» are not the same answer, and collapsing them is the bug this shape exists
 * to prevent: an `organizationName: 123` that becomes `''` reads as «this template names no
 * organisation», which quietly hands the certificate back to the issuer. Only `undefined`, `null`
 * and a string are a value here; anything else is a refusal on that field.
 */
type StoredField = { ok: true; value: string } | { ok: false };

const readField = (v: unknown): StoredField =>
  v === undefined || v === null
    ? { ok: true, value: '' }
    : typeof v === 'string'
      ? { ok: true, value: v.trim() }
      : { ok: false };

/**
 * The Page address, read with the same parser the browser will use and reduced to the one shape
 * every consumer compares: `https://www.linkedin.com/company/<slug>/`.
 *
 * Normalising rather than refusing is deliberate for the parts LinkedIn itself varies — a missing
 * scheme, a `tr.` locale host, a `?originalSubdomain=` the share menu appends — because those are
 * what an administrator actually copies out of a browser, and `samePage` in the verified directory
 * already treats them as the same Page. What is NOT normalised away is anything that would change
 * WHICH page it is: another host, a personal profile, a missing slug, an embedded credential.
 */
function readCompanyPage(
  raw: string,
): { ok: true; value: string } | { ok: false; code: string; message: string } {
  const candidate = /^[a-z][a-z0-9+.-]*:\/\//i.test(raw) ? raw : `https://${raw}`;
  let u: URL;
  try {
    u = new URL(candidate);
  } catch {
    return {
      ok: false,
      code: 'linkedin_issuer_page_invalid',
      message: 'must be a LinkedIn company page address',
    };
  }
  if (u.protocol !== 'http:' && u.protocol !== 'https:')
    return {
      ok: false,
      code: 'linkedin_issuer_page_invalid',
      message: 'must be an http or https address',
    };
  if (u.username !== '' || u.password !== '')
    return {
      ok: false,
      code: 'linkedin_issuer_page_carries_credentials',
      message: 'must not carry a username or password',
    };
  if (!/(^|\.)linkedin\.com$/i.test(u.hostname))
    return {
      ok: false,
      code: 'linkedin_issuer_page_not_linkedin',
      message: 'must be an address on linkedin.com',
    };
  const segments = u.pathname.split('/').filter((s) => s !== '');
  const slug = segments[1];
  /*
   * The company and its slug, and whatever tab the address was copied from dropped.
   *
   * `/company/boyner/posts/` and `/company/boyner/about/` are the same Page seen from different
   * tabs, and that is what an administrator's clipboard usually holds; reducing them to the Page
   * itself is also what makes the verified directory able to recognise it at all, since it
   * compares whole paths. Anything that is not `/company/<slug>` — a personal profile, the bare
   * company root, some other section of the site — is a different thing and is refused.
   */
  if (segments.length < 2 || segments[0]?.toLowerCase() !== 'company' || !slug)
    return {
      ok: false,
      code: 'linkedin_issuer_page_not_company',
      message: 'must be a linkedin.com/company/… page',
    };
  return { ok: true, value: `https://www.linkedin.com/company/${slug}/` };
}

/**
 * The override as it may be stored: both fields empty means there is none, and anything that is
 * not a complete, addressable organisation is refused on the field it is wrong on.
 *
 * Fail-closed (rule 11): a bad Page address never degrades into «use the issuer instead», because
 * an administrator who typed a customer's Page and got the issuer's certificate back would have
 * no way of telling that their setting was dropped.
 */
export function normalizeLinkedInIssuingOrganization(input: unknown): IssuingOrganizationValidation {
  if (input === null || input === undefined) return { ok: true, value: null };
  if (typeof input !== 'object' || Array.isArray(input))
    return {
      ok: false,
      errors: [
        {
          key: LINKEDIN_ISSUER_KEY,
          code: 'linkedin_issuer_malformed',
          message: 'must be an object with organizationName and pageUrl',
        },
      ],
    };
  const raw = input as Record<string, unknown>;
  const nameField = readField(raw.organizationName);
  const pageField = readField(raw.pageUrl);

  const errors: IssuingOrganizationFieldError[] = [];
  /*
   * A value of the wrong type is named first and alone: every later question — «is it
   * empty?», «is it a company page?» — would otherwise be answered about a value that was
   * never there, and the honest answer to all of them is that the field has to be text.
   */
  if (!nameField.ok)
    errors.push({ key: NAME_KEY, code: 'linkedin_issuer_name_not_text', message: 'must be text' });
  if (!pageField.ok)
    errors.push({ key: PAGE_KEY, code: 'linkedin_issuer_page_not_text', message: 'must be text' });
  if (errors.length > 0) return { ok: false, errors };

  const name = nameField.ok ? nameField.value : '';
  const page = pageField.ok ? pageField.value : '';
  if (name === '' && page === '') return { ok: true, value: null };

  if (name === '')
    errors.push({
      key: NAME_KEY,
      code: 'linkedin_issuer_name_required',
      message: 'an organisation page needs the organisation name beside it',
    });
  else if (name.length > MAX_NAME)
    errors.push({
      key: NAME_KEY,
      code: 'linkedin_issuer_name_too_long',
      message: `must be at most ${MAX_NAME} characters`,
    });

  let pageUrl: string | null = null;
  if (page !== '') {
    const read = readCompanyPage(page);
    if (read.ok) pageUrl = read.value;
    else errors.push({ key: PAGE_KEY, code: read.code, message: read.message });
  }

  if (errors.length > 0) return { ok: false, errors };
  return { ok: true, value: { organizationName: name, pageUrl } };
}

/**
 * What a stored template model says about the issuing organisation — three answers, not two.
 *
 * A model is free-form JSON that has been through a database and two packages, so this re-runs the
 * same rules rather than trusting what it finds. It used to collapse a broken row into `null`,
 * which means «no override» and therefore «describe the issuer»: a certificate issued for somebody
 * else would have gone out naming Happy Place to Work because a stored value was the wrong type.
 * A row that cannot be read is now its own answer, and every caller has to decide what to do with
 * it — which, everywhere LinkedIn is concerned, is to withhold the profile-add rather than guess.
 */
export type StoredIssuingOrganization =
  /** The template names no organisation: the documented default, and the common case. */
  | { status: 'none' }
  /** The template names one, and it is usable. */
  | { status: 'custom'; value: LinkedInIssuingOrganization }
  /** Something is stored and it is not a usable organisation. Never treat this as «none». */
  | { status: 'unreadable'; errors: IssuingOrganizationFieldError[] };

export function readLinkedInIssuingOrganization(model: unknown): StoredIssuingOrganization {
  if (model === null || typeof model !== 'object' || Array.isArray(model)) return { status: 'none' };
  const stored = (model as Record<string, unknown>)[LINKEDIN_ISSUER_KEY];
  if (stored === undefined || stored === null) return { status: 'none' };
  const read = normalizeLinkedInIssuingOrganization(stored);
  if (!read.ok) return { status: 'unreadable', errors: read.errors };
  return read.value === null ? { status: 'none' } : { status: 'custom', value: read.value };
}

export interface IssuerIdentity {
  organizationName: string;
  pageUrl: string | null;
  organizationId: string | null;
}

/**
 * One identity, chosen whole.
 *
 * When the template names its own organisation, the id is looked up from THAT organisation's Page
 * and from nowhere else — `verifiedOrganizationIdForPage` answers only for the Page it was
 * verified against, so an unknown Page yields null and the link falls back to the name. The
 * issuer's own number is unreachable from this branch by construction.
 */
export function resolveIssuingOrganization(
  override: LinkedInIssuingOrganization | null,
  issuer: IssuerIdentity,
): ResolvedIssuingOrganization {
  if (override)
    return {
      source: 'template',
      organizationName: override.organizationName,
      pageUrl: override.pageUrl,
      organizationId: verifiedOrganizationIdForPage(override.pageUrl),
    };
  return {
    source: 'issuer',
    organizationName: issuer.organizationName,
    pageUrl: issuer.pageUrl,
    organizationId: issuer.organizationId,
  };
}

/**
 * The same choice, over what a stored model actually says.
 *
 * This is the entry point every LinkedIn surface should use: `none` answers with the issuer, as
 * it always has, `custom` answers with the template's own organisation, whole, and `unreadable`
 * answers with neither. Nothing else about the certificate — its verification page, its artifact,
 * its download — depends on this, and none of them are withheld by it.
 */
export function resolveStoredIssuingOrganization(
  stored: StoredIssuingOrganization,
  issuer: IssuerIdentity,
): ResolvedIssuingOrganization {
  if (stored.status === 'unreadable')
    return { source: 'unreadable', organizationName: '', pageUrl: null, organizationId: null };
  return resolveIssuingOrganization(stored.status === 'custom' ? stored.value : null, issuer);
}

/* ------------------------------------------------------------------ the product's own shorthand */

/**
 * The two identities a credential can be described by, as the screens already hold them.
 *
 * Structural on purpose: the demo store, the API projection and the public projection all carry
 * these fields, and none of them should have to be imported here for the rule to be one rule.
 */
export interface ReleaseLinkedInIdentity {
  /** The organisation the template pinned into THIS release, when it named one. */
  linkedInIssuer?: LinkedInIssuingOrganization | null;
  /**
   * The release stores something in that slot and it is not a usable organisation.
   *
   * Carried beside the value rather than inside it because the editor's own working copy uses the
   * same field for what the author is typing. `true` means the same thing `status: 'unreadable'`
   * means on the server: name nobody, and withhold the profile-add.
   */
  linkedInIssuerUnreadable?: boolean;
  issuerProfileSnapshot: {
    linkedinOrganizationId?: string | null;
    linkedinPageUrl: string;
    linkedinIssuerName: string;
  };
}

export interface SettingsLinkedInIdentity {
  linkedinOrganizationId?: string | null;
  linkedinPageUrl: string;
  linkedinIssuerName: string;
}

/**
 * Which organisation this credential names on LinkedIn — the single answer every surface uses.
 *
 * Without an override this is exactly the expression the screens used before: the record's own
 * release snapshot first, today's settings only where the snapshot is empty. With one, it is the
 * template's organisation, whole.
 */
export function credentialIssuingOrganization(
  release: ReleaseLinkedInIdentity,
  settings: SettingsLinkedInIdentity,
): ResolvedIssuingOrganization {
  const snapshot = release.issuerProfileSnapshot;
  const stored: StoredIssuingOrganization =
    release.linkedInIssuerUnreadable === true
      ? { status: 'unreadable', errors: [] }
      : release.linkedInIssuer
        ? { status: 'custom', value: release.linkedInIssuer }
        : { status: 'none' };
  return resolveStoredIssuingOrganization(stored, {
    organizationName: snapshot.linkedinIssuerName || settings.linkedinIssuerName,
    pageUrl: snapshot.linkedinPageUrl || settings.linkedinPageUrl || null,
    organizationId: snapshot.linkedinOrganizationId ?? settings.linkedinOrganizationId ?? null,
  });
}

/**
 * The same answer in the shape `addToProfileLinkFor` takes.
 *
 * Deliberately NOT spread from the issuer with the name replaced: every one of the three values
 * comes from the identity that answered, so a custom organisation can never inherit the issuer's
 * number or Page.
 */
export function linkedInIssuerConfigFor(
  release: ReleaseLinkedInIdentity,
  settings: SettingsLinkedInIdentity,
): {
  linkedinOrganizationId: string | null;
  linkedinPageUrl: string | null;
  linkedinIssuerName: string;
} | null {
  const resolved = credentialIssuingOrganization(release, settings);
  /* Null rather than an empty organisation: there is no honest link to build from a record whose
     stored organisation cannot be read, and an empty name would simply omit the field. */
  if (resolved.source === 'unreadable') return null;
  return {
    linkedinOrganizationId: resolved.organizationId,
    linkedinPageUrl: resolved.pageUrl,
    linkedinIssuerName: resolved.organizationName,
  };
}

/** What `missing` names when the record's own organisation is the thing that is wrong. */
export const ISSUING_ORGANIZATION_UNREADABLE = 'issuingOrganization';

/**
 * The Add-to-Profile link for a credential, refused when its organisation cannot be read.
 *
 * One call instead of «resolve, check, build» at three screens, because the check is the part a
 * screen would forget. A refusal looks exactly like any other incomplete record — `url: null`
 * with a named `missing` — so every surface already handles it: no tab is opened onto an empty
 * LinkedIn form, and nothing else about the certificate changes.
 */
export function addToProfileLinkForCredential(
  credential: CredentialForLinkedIn,
  title: string,
  release: ReleaseLinkedInIdentity,
  settings: SettingsLinkedInIdentity,
): AddToProfileLink {
  const config = linkedInIssuerConfigFor(release, settings);
  if (config === null)
    return { url: null, missing: [ISSUING_ORGANIZATION_UNREADABLE], organizationMode: 'none' };
  return addToProfileLinkFor(credential, title, config);
}

/**
 * The refusal codes, in the product's own words, keyed by the box they belong on.
 *
 * The message is deliberately about what to do — «linkedin.com/company/… biçiminde» rather than
 * «url_invalid» — because the person reading it pasted an address and needs to know which one to
 * paste instead. The server refuses the same cases with the same codes; this only decides where
 * the sentence appears.
 */
const ISSUER_MESSAGES: Record<string, string> = {
  linkedin_issuer_name_required: 'Kuruluş sayfası girildiğinde kuruluş adı da gerekir.',
  linkedin_issuer_name_too_long: 'Kuruluş adı çok uzun.',
  linkedin_issuer_page_invalid: 'Adres okunamadı. LinkedIn kurum sayfasının tam adresini yapıştırın.',
  linkedin_issuer_page_not_linkedin: 'Adres linkedin.com üzerinde olmalı.',
  linkedin_issuer_page_not_company: 'Adres bir kurum sayfası olmalı: linkedin.com/company/… biçiminde.',
  linkedin_issuer_page_carries_credentials: 'Adres kullanıcı adı veya parola taşımamalı.',
  linkedin_issuer_malformed: 'Kuruluş bilgisi okunamadı.',
};

export interface LinkedInIssuerFieldMessages {
  organizationName?: string;
  pageUrl?: string;
}

/** The same rule the server applies, run for the message only — the server is still the gate. */
export function linkedInIssuerFieldErrors(value: unknown): LinkedInIssuerFieldMessages {
  const read = normalizeLinkedInIssuingOrganization(value ?? null);
  if (read.ok) return {};
  const out: LinkedInIssuerFieldMessages = {};
  for (const e of read.errors) {
    const message = ISSUER_MESSAGES[e.code] ?? e.message;
    if (e.key.endsWith('.pageUrl')) out.pageUrl = message;
    else out.organizationName = message;
  }
  return out;
}
